import { Box, Container, Typography, Grid, Card, CardContent, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import TimelineIcon from '@mui/icons-material/Timeline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FeatureBadge from './FeatureBadge';

function FeaturesShowcase() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const featureCategories = [
    {
      title: t('landing.features.insulinManagement.title'),
      icon: <VaccinesIcon sx={{ fontSize: 40 }} />,
      color: '#90caf9',
      features: [
        t('landing.features.insulinManagement.unlimitedProfiles'),
        t('landing.features.insulinManagement.timeBasedSegments'),
        t('landing.features.insulinManagement.profileChangelog'),
        t('landing.features.insulinManagement.iobCalculator'),
      ],
    },
    {
      title: t('landing.features.cgmIntegration.title'),
      icon: <DeviceHubIcon sx={{ fontSize: 40 }} />,
      color: '#f48fb1',
      features: [
        t('landing.features.cgmIntegration.dexcomShare'),
        t('landing.features.cgmIntegration.nightscoutSupport'),
        t('landing.features.cgmIntegration.realTimeMonitoring'),
        t('landing.features.cgmIntegration.glucoseHistory'),
      ],
    },
    {
      title: t('landing.features.trackingLogging.title'),
      icon: <TimelineIcon sx={{ fontSize: 40 }} />,
      color: '#ffa726',
      features: [
        t('landing.features.trackingLogging.unlimitedSensors'),
        t('landing.features.trackingLogging.ketoneMonitoring'),
        t('landing.features.trackingLogging.hba1cTracking'),
        t('landing.features.trackingLogging.foodDatabase'),
      ],
    },
    {
      title: t('landing.features.comingSoon.title'),
      icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
      color: '#66bb6a',
      features: [
        t('landing.features.comingSoon.aiInsights'),
      ],
      isComingSoon: true,
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        background: '#212529',
        position: 'relative',
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              mb: 2,
              color: '#FFFFFF',
            }}
          >
            {t('landing.features.title')}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#ADB5BD',
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            {t('landing.features.subtitle')}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {featureCategories.map((category, index) => (
            <Grid item xs={12} sm={6} md={6} lg={3} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  background: `linear-gradient(135deg, ${category.color}08 0%, transparent 100%)`,
                  border: `1px solid ${category.color}20`,
                  borderRadius: 3,
                  position: 'relative',
                  overflow: 'visible',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-12px) scale(1.02)',
                    border: `1px solid ${category.color}40`,
                    boxShadow: `0 24px 48px ${category.color}25`,
                    '& .feature-icon-box': {
                      transform: 'rotate(5deg) scale(1.1)',
                    },
                  },
                }}
              >
                {/* Floating badge */}
                {category.isComingSoon && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -12,
                      right: 16,
                      zIndex: 1,
                    }}
                  >
                    <FeatureBadge type="coming" />
                  </Box>
                )}

                <CardContent sx={{ p: 3 }}>
                  {/* Icon */}
                  <Box
                    className="feature-icon-box"
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '20px',
                      background: `linear-gradient(135deg, ${category.color}20, ${category.color}05)`,
                      border: `2px solid ${category.color}30`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      color: category.color,
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: -8,
                        background: `radial-gradient(circle, ${category.color}15, transparent)`,
                        borderRadius: '25px',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                      },
                      '&:hover::before': {
                        opacity: 1,
                      },
                    }}
                  >
                    {category.icon}
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 3,
                      color: '#FFFFFF',
                      fontSize: '1.2rem',
                    }}
                  >
                    {category.title}
                  </Typography>

                  {/* Feature list */}
                  <Stack spacing={1.5}>
                    {category.features.map((feature, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <CheckCircleIcon
                          sx={{
                            fontSize: 18,
                            color: category.color,
                            mt: 0.2,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#DEE2E6',
                            lineHeight: 1.6,
                            fontSize: '0.9rem',
                          }}
                        >
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>

                {/* Decorative corner glow */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 100,
                    height: 100,
                    background: `radial-gradient(circle at bottom right, ${category.color}15, transparent 70%)`,
                    pointerEvents: 'none',
                    borderBottomRightRadius: 12,
                  }}
                />
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => {
              window.scrollTo(0, 0);
              navigate('/pricing');
            }}
            sx={{
              py: 1.5,
              px: 4,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              borderColor: '#90caf9',
              color: '#90caf9',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#90caf9',
                background: 'rgba(144, 202, 249, 0.08)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            {t('landing.features.ctaPricing')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default FeaturesShowcase;
